import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ComposedChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, Bar } from 'recharts';
import { AlertCircle, Settings, TrendingUp } from 'lucide-react';
import { useComponentMetrics, getMetricObjectiveClassName, saveChartConfig } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';
import PianoChannelsChart from './PianoChannelsChart';
import PianoGeographyChart from './PianoGeographyChart';
import RepartitionChart from './RepartitionChart';
// Les mêmes icônes que les composants Google Analytics équivalents.
import { Monitor, Globe, Languages, Calendar, FileText } from 'lucide-react';
import { marqueursDeNotes, useNotesDeGraphique, InfobulleDeNotes } from './notes/marqueursDeNotes';

// Classification des métriques Piano : VALEURS (pour histogramme) vs RATIOS (pour courbe)
// Les métriques ajoutées pour aligner les trois solutions de mesure doivent être déclarées
// **partout** : classification, correspondance de clés, configuration des barres et de la
// courbe, et lecture de la valeur. En oublier une fait retomber le graphique sur sa métrique
// par défaut — la carte se surligne, et la courbe montre autre chose.
const PIANO_VALUE_METRICS = ['visites', 'visiteurs', 'pages_vues', 'conv', 'valeur_conv', 'transactions', 'chiffre_affaires'];
const PIANO_RATIO_METRICS = ['taux_conv', 'rebond', 'duree_moy', 'panier_moyen', 'pages_visite', 'taux_transaction'];

// Mapping clé KPI → clé graphique
const PIANO_METRIC_KEY_MAP = {
  visites: 'visits',
  pages_vues: 'pageViews',
  conv: 'conversions',
  valeur_conv: 'conversionsValue',
  taux_conv: 'conversionRate',
  rebond: 'bounceRate',
  duree_moy: 'avgTimeOnSite',
  panier_moyen: 'avgOrderValue',
  visiteurs: 'uniqueVisitors',
  pages_visite: 'actionsPerVisit',
  transactions: 'transactions',
  chiffre_affaires: 'totalRevenue',
  taux_transaction: 'transactionRate'
};

/**
 * PianoPerformanceChart - Composant de graphique pour les métriques Piano Analytics
 * Style identique à GA4 PerformanceChart
 *
 * Métriques affichées (même structure que GA4):
 * 1. Sessions (visites)
 * 2. Taux de Conversion
 * 3. Conversions (événements/goals)
 * 4. Pages vues
 * 5. % de Rebond
 * 6. Durée moyenne
 * 7. Valeur de Conversion
 * 8. Panier moyen
 */

const PianoPerformanceChart = ({
  data,
  /**
   * Notes à reporter sur le graphique.
   *
   * Le mécanisme n'existait que sur les dashboards de régies : une note cochée « afficher
   * sur les graphiques » et rattachée à une propriété analytique ne s'affichait nulle part.
   */
  notes = [],
  summary,
  previousPeriodData,
  channels = [],
  geography = [],
  devices = [],
  browsers = [],
  languages = [],
  pages = [],
  weekdays = [],
  ecommerce,
  granularity = 'day',
  onGranularityChange,
  loading = false,
  KPICard,
  formatNumber,
  // Props pour le drill-down géographique
  siteId = null,
  token = null,
  period = 'LAST_30_DAYS',
  customStartDate = null,
  customEndDate = null,
  // Props pour la configuration des métriques
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'piano_results',
  // Props pour la comparaison
  compareEnabled = false,
  comparisonType = ''
}) => {
  const [showPerformanceChart, setShowPerformanceChart] = useState(true);
  const [selectedLineMetric, setSelectedLineMetric] = useState('conversionRate'); // RATIO par défaut
  const [selectedBarMetric, setSelectedBarMetric] = useState('conversions'); // VALEUR par défaut
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Normaliser les données de la période précédente/comparaison
  const prevSummary = useMemo(() => {
    if (!previousPeriodData) return null;
    return previousPeriodData.summary || previousPeriodData;
  }, [previousPeriodData]);

  // Label pour la période de comparaison
  const comparisonLabel = comparisonType === 'previous_year' ? 'année précédente' : 'période précédente';

  // Hook pour les métriques du composant Résultats (réactif aux changements)
  const resultsMetrics = useComponentMetrics('piano_results', accountId);

  // Fonction helper pour vérifier si une métrique est sélectionnée pour Résultats
  const isResultsMetricActive = useCallback((metricKey) => {
    return resultsMetrics.metrics.includes(metricKey);
  }, [resultsMetrics.metrics]);

  // Filtrer les métriques disponibles selon celles activées dans "Configurer les métriques"
  const enabledMetrics = resultsMetrics?.metrics || [];
  const availableValueMetrics = useMemo(() =>
    PIANO_VALUE_METRICS.filter(m => enabledMetrics.includes(m)),
    [enabledMetrics]
  );
  // La courbe peut afficher TOUTES les métriques (VALEURS + RATIOS)
  const availableLineMetrics = useMemo(() =>
    [...PIANO_VALUE_METRICS, ...PIANO_RATIO_METRICS].filter(m => enabledMetrics.includes(m)),
    [enabledMetrics]
  );

  /**
   * Dernière configuration de graphe appliquée, pour ne pas écraser un choix manuel à chaque
   * rendu — mais reprendre la main dès qu'un template est appliqué.
   *
   * Un simple booléen vivait ici : la configuration n'était posée qu'une fois par montage, si
   * bien que changer de template en cours de session laissait le graphe inchangé. La comparer
   * permet de distinguer « déjà fait » de « redemandé ».
   */
  const chartConfigAppliedRef = useRef(null);

  /**
   * Choisir la métrique des barres ou de la courbe, **et s'en souvenir**.
   *
   * Les deux `setState` vivaient dans chaque carte. Le réglage ne survivait donc pas au
   * rechargement : seul un modèle écrivait un `chartConfig`, jamais un clic.
   */
  const choisirMetriqueGraphe = useCallback((barres, courbe) => {
    setSelectedBarMetric(barres);
    setSelectedLineMetric(courbe);
    // La référence suit, sinon l'effet de configuration rejouerait le couple qu'on vient
    // d'écrire — sans dommage, mais pour rien.
    chartConfigAppliedRef.current = JSON.stringify({ barMetric: barres, lineMetric: courbe });
    saveChartConfig('piano_results', { barMetric: barres, lineMetric: courbe }, accountId);
  }, [accountId]);

  useEffect(() => {
    const chartConfig = resultsMetrics?.chartConfig;
    if (!chartConfig) return;

    const configKey = JSON.stringify(chartConfig);
    if (chartConfigAppliedRef.current === configKey) return;

    const { barMetric, lineMetric } = chartConfig;
    if (barMetric) setSelectedBarMetric(barMetric);
    if (lineMetric) setSelectedLineMetric(lineMetric);
    chartConfigAppliedRef.current = configKey;
  }, [resultsMetrics?.chartConfig]);

  // Synchroniser les métriques sélectionnées avec les métriques disponibles
  useEffect(() => {
    // Pour la courbe : vérifier que la métrique sélectionnée est disponible parmi TOUTES les métriques
    const currentLineKpiKey = Object.keys(PIANO_METRIC_KEY_MAP).find(k => PIANO_METRIC_KEY_MAP[k] === selectedLineMetric);
    if (currentLineKpiKey && !availableLineMetrics.includes(currentLineKpiKey)) {
      // Fallback : préférer un ratio, sinon une valeur
      const firstAvailableRatio = availableLineMetrics.find(m => PIANO_RATIO_METRICS.includes(m));
      const firstAvailable = firstAvailableRatio || availableLineMetrics[0];
      if (firstAvailable) {
        setSelectedLineMetric(PIANO_METRIC_KEY_MAP[firstAvailable]);
      }
    }

    // Pour l'histogramme : vérifier que la métrique sélectionnée est disponible (VALEUR ou RATIO)
    const currentBarKpiKey = Object.keys(PIANO_METRIC_KEY_MAP).find(k => PIANO_METRIC_KEY_MAP[k] === selectedBarMetric);
    if (currentBarKpiKey && !availableLineMetrics.includes(currentBarKpiKey)) {
      // Sélectionner la première valeur disponible, sinon un ratio
      const firstAvailableValue = availableLineMetrics.find(m => PIANO_VALUE_METRICS.includes(m));
      const firstAvailable = firstAvailableValue || availableLineMetrics[0];
      if (firstAvailable) {
        setSelectedBarMetric(PIANO_METRIC_KEY_MAP[firstAvailable]);
      }
    }
  }, [availableLineMetrics, availableValueMetrics, selectedLineMetric, selectedBarMetric]);

  // Formateur de temps (secondes -> mm:ss)
  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Configuration des métriques pour la courbe (même que GA4)
  const lineMetricConfig = {
    visits: { label: 'Sessions', color: '#82ca9d', unit: '', format: (val) => formatNumber(val) },
    conversionsValue: { label: 'Valeur Conv.', color: '#06b6d4', unit: '€', format: (val) => formatNumber(val, 2) },
    pageViews: { label: 'Pages vues', color: '#10b981', unit: '', format: (val) => formatNumber(val) },
    bounceRate: { label: 'Rebond', color: '#8b5cf6', unit: '%', format: (val) => formatNumber(val, 1) },
    avgTimeOnSite: { label: 'Durée moy.', color: '#f97316', unit: '', format: (val) => formatTime(val) },
    avgOrderValue: { label: 'Panier moyen', color: '#ec4899', unit: '€', format: (val) => formatNumber(val, 2) },
    conversionRate: { label: 'Taux de conv.', color: '#f59e0b', unit: '%', format: (val) => formatNumber(val, 2) },
    conversions: { label: 'Conversions', color: '#8884d8', unit: '', format: (val) => formatNumber(val) },
    uniqueVisitors: { label: 'Utilisateurs', color: '#8b5cf6', unit: '', format: (val) => formatNumber(val) },
    actionsPerVisit: { label: 'Pages/Session', color: '#14b8a6', unit: '', format: (val) => formatNumber(val, 2) },
    transactions: { label: 'Transactions', color: '#10b981', unit: '', format: (val) => formatNumber(val) },
    totalRevenue: { label: 'Chiffre d\'affaires', color: '#06b6d4', unit: '€', format: (val) => formatNumber(val, 2) },
    transactionRate: { label: '% Conversion (transactions)', color: '#f97316', unit: '%', format: (val) => formatNumber(val, 2) }
  };

  // Obtenir la valeur de la métrique sélectionnée pour chaque point
  const getLineMetricValue = (d) => {
    switch (selectedLineMetric) {
      case 'visits': return d.visits || 0;
      case 'conversionsValue': return d.conversionsValue || d.revenue || 0;
      case 'pageViews': return d.pageViews || 0;
      case 'bounceRate': return d.bounceRate || 0;
      case 'avgTimeOnSite': return d.avgTimeOnSite || 0;
      case 'avgOrderValue': return d.conversions > 0 ? (d.conversionsValue || 0) / d.conversions : 0;
      case 'conversionRate': return d.visits > 0 ? ((d.conversions || 0) / d.visits * 100) : 0;
      case 'conversions': return d.conversions || 0;
      case 'uniqueVisitors': return d.visitors || 0;
      case 'actionsPerVisit': return d.visits > 0 ? (d.pageViews || 0) / d.visits : 0;
      case 'transactions': return d.transactions || 0;
      case 'totalRevenue': return d.totalRevenue || 0;
      case 'transactionRate': return d.visits > 0 ? ((d.transactions || 0) / d.visits * 100) : 0;
      default: return d.visits || 0;
    }
  };

  // Fonction pour calculer la valeur de la métrique des barres
  const getBarMetricValue = (d) => {
    switch (selectedBarMetric) {
      case 'visits': return d.visits || 0;
      case 'conversionsValue': return d.conversionsValue || d.revenue || 0;
      case 'pageViews': return d.pageViews || 0;
      case 'conversions': return d.conversions || 0;
      // RATIOs
      case 'bounceRate': return d.bounceRate || 0;
      case 'avgTimeOnSite': return d.avgTimeOnSite || 0;
      case 'avgOrderValue': return d.conversions > 0 ? (d.conversionsValue || 0) / d.conversions : 0;
      case 'conversionRate': return d.visits > 0 ? ((d.conversions || 0) / d.visits * 100) : 0;
      case 'uniqueVisitors': return d.visitors || 0;
      case 'actionsPerVisit': return d.visits > 0 ? (d.pageViews || 0) / d.visits : 0;
      case 'transactions': return d.transactions || 0;
      case 'totalRevenue': return d.totalRevenue || 0;
      case 'transactionRate': return d.visits > 0 ? ((d.transactions || 0) / d.visits * 100) : 0;
      default: return d.conversions || 0;
    }
  };

  // Formater les labels de date selon la granularité
  const formatDateLabel = (dateStr, gran) => {
    if (!dateStr) return '';

    switch (gran) {
      case 'week':
        return `Sem. ${dateStr}`;
      case 'month':
        if (dateStr.includes('/')) return dateStr;
        if (dateStr.includes('-') && dateStr.length === 7) {
          const [year, month] = dateStr.split('-');
          return `${month}/${year}`;
        }
        return dateStr;
      case 'quarter':
      case 'year':
        return dateStr;
      default: // day
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          const date = new Date(dateStr);
          return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        }
        return dateStr;
    }
  };

  // Données du graphique avec métrique sélectionnée
  const { survolee, position, onSurvol, onSortie } = useNotesDeGraphique();

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(d => ({
      ...d,
      lineMetricValue: getLineMetricValue(d),
      barMetricValue: getBarMetricValue(d)
    }));
  }, [data, selectedLineMetric, selectedBarMetric]);

  // Calculer les KPI à partir du summary ou des données
  /**
   * Une carte de métrique, avec sa bascule barres / courbe.
   *
   * Les cartes historiques sont écrites à la main, chacune répétant la même quarantaine de
   * lignes. Les métriques ajoutées pour aligner Piano sur GA4 et Matomo passent par ce rendu :
   * cinq de plus en copie intégrale auraient fait deux cents lignes dont la seule différence
   * tient en six valeurs.
   */
  const carteMetrique = ({ cle, champ, titre, valeur, couleur, unite, decimales = 0, serie, sansSerie, nonMesure, mention }) => {
    if (!isResultsMetricActive(cle)) return null;

    // Deux empêchements distincts, longtemps confondus : une carte **sans courbe** garde son
    // chiffre et perd seulement sa bascule ; une carte **non mesurée** affiche un tiret. Les
    // traiter ensemble avait vidé la carte Utilisateurs, dont la valeur existe pourtant.
    const figee = sansSerie || nonMesure;

    const basculer = () => {
      if (selectedBarMetric === champ) {
        // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
        choisirMetriqueGraphe(selectedLineMetric || champ, champ);
        return;
      }
      choisirMetriqueGraphe(champ, selectedBarMetric || selectedLineMetric);
    };

    const choisie = selectedBarMetric === champ || selectedLineMetric === champ;

    return (
      <div
        onClick={figee ? undefined : basculer}
        style={{
          cursor: figee ? 'default' : 'pointer',
          borderRadius: '8px',
          outline: choisie && !figee ? `3px solid ${couleur}` : 'none',
          outlineOffset: '2px',
          transition: 'outline 0.2s'
        }}
        title={mention
          || (selectedBarMetric === champ ? 'Affiché en barres' : selectedLineMetric === champ ? 'Affiché en courbe' : 'Cliquez pour afficher en barres')}
      >
        <KPICard
          title={titre}
          value={nonMesure ? 0 : valeur}
          unit={nonMesure ? undefined : unite}
          color={couleur}
          currentPeriodData={figee ? [] : (data?.map(serie || (d => d[champ] || 0)) || [])}
          previousValue={nonMesure ? undefined : prevSummary?.[champ]}
          comparisonLabel={comparisonLabel}
          formatter={(val) => (nonMesure ? '—' : formatNumber(val, decimales))}
          className={getMetricObjectiveClassName(cle, valeur, 'piano_results', accountId)}
        />
      </div>
    );
  };

  const kpiData = useMemo(() => {
    // Utiliser le summary si disponible
    if (summary) {
      const totalVisits = summary.visits || summary.totalVisits || 0;
      const totalPageviews = summary.pageViews || summary.totalPageviews || 0;
      const totalConversions = summary.conversions || 0;
      const totalConversionsValue = summary.conversionsValue || summary.revenue || 0;

      return {
        totalVisits,
        totalPageviews,
        totalConversions,
        totalConversionsValue,
        avgBounceRate: summary.bounceRate || 0,
        avgTimeOnSite: summary.avgTimeOnSite || 0,
        // Le panier moyen se rapporte aux **transactions**, et non à l'événement choisi :
        // diviser un chiffre d'affaires par des ajouts au panier ne donne pas un panier.
        avgOrderValue: summary.avgOrderValue
          ?? ((summary.transactions || 0) > 0 ? (summary.totalRevenue || 0) / summary.transactions : 0),
        conversionRate: totalVisits > 0 ? (totalConversions / totalVisits * 100) : 0,
        // `null` et non zéro : l'API peut ne pas rendre la mesure, et « non mesuré » n'est
        // pas « personne ».
        uniqueVisitors: summary.uniqueVisitors ?? null,
        avgActionsPerVisit: summary.actionsPerVisit
          ?? (totalVisits > 0 ? totalPageviews / totalVisits : 0),
        totalTransactions: summary.transactions || 0,
        totalRevenue: summary.totalRevenue || 0,
        transactionRate: summary.transactionRate
          ?? (totalVisits > 0 ? ((summary.transactions || 0) / totalVisits * 100) : 0)
      };
    }

    // Sinon calculer depuis les données quotidiennes
    if (!data || data.length === 0) {
      return {
        totalVisits: 0,
        totalPageviews: 0,
        totalConversions: 0,
        totalConversionsValue: 0,
        avgBounceRate: 0,
        avgTimeOnSite: 0,
        avgOrderValue: 0,
        conversionRate: 0,
        uniqueVisitors: null,
        avgActionsPerVisit: 0,
        totalTransactions: 0,
        totalRevenue: 0,
        transactionRate: 0
      };
    }

    const totals = data.reduce((acc, d) => ({
      visits: acc.visits + (d.visits || 0),
      pageViews: acc.pageViews + (d.pageViews || 0),
      conversions: acc.conversions + (d.conversions || 0),
      conversionsValue: acc.conversionsValue + (d.conversionsValue || d.revenue || 0),
      transactions: acc.transactions + (d.transactions || 0),
      totalRevenue: acc.totalRevenue + (d.totalRevenue || 0),
      bounceSum: acc.bounceSum + ((d.bounceRate || 0) * (d.visits || 0)),
      timeSum: acc.timeSum + ((d.avgTimeOnSite || 0) * (d.visits || 0))
    }), { visits: 0, pageViews: 0, conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0, bounceSum: 0, timeSum: 0 });

    return {
      totalVisits: totals.visits,
      totalPageviews: totals.pageViews,
      totalConversions: totals.conversions,
      totalConversionsValue: totals.conversionsValue,
      avgBounceRate: totals.visits > 0 ? totals.bounceSum / totals.visits : 0,
      avgTimeOnSite: totals.visits > 0 ? totals.timeSum / totals.visits : 0,
      avgOrderValue: totals.transactions > 0 ? totals.totalRevenue / totals.transactions : 0,
      conversionRate: totals.visits > 0 ? (totals.conversions / totals.visits * 100) : 0,
      // Pas de visiteurs uniques reconstituables depuis le quotidien : la métrique n'est pas
      // additive, un visiteur revenu cinq jours compterait cinq fois.
      uniqueVisitors: null,
      avgActionsPerVisit: totals.visits > 0 ? totals.pageViews / totals.visits : 0,
      totalTransactions: totals.transactions,
      totalRevenue: totals.totalRevenue,
      transactionRate: totals.visits > 0 ? (totals.transactions / totals.visits * 100) : 0
    };
  }, [data, summary]);

  // Configuration métrique courbe actuelle
  const currentLineConfig = lineMetricConfig[selectedLineMetric];

  // Configuration des barres (pour TOUTES les métriques : VALEURS + RATIOS)
  const barMetricConfig = useMemo(() => {
    const configs = {
      visits: { name: 'Sessions', fill: '#82ca9d' },
      pageViews: { name: 'Pages vues', fill: '#10b981' },
      conversionsValue: { name: 'Valeur Conv.', fill: '#06b6d4' },
      conversions: { name: 'Conversions', fill: '#8884d8' },
      // RATIOs
      conversionRate: { name: 'Taux de conv.', fill: '#f59e0b' },
      bounceRate: { name: 'Rebond', fill: '#8b5cf6' },
      avgTimeOnSite: { name: 'Durée moy.', fill: '#f97316' },
      avgOrderValue: { name: 'Panier moyen', fill: '#ec4899' },
      uniqueVisitors: { name: 'Utilisateurs', fill: '#8b5cf6' },
      actionsPerVisit: { name: 'Pages/Session', fill: '#14b8a6' },
      transactions: { name: 'Transactions', fill: '#10b981' },
      totalRevenue: { name: 'Chiffre d\'affaires', fill: '#06b6d4' },
      transactionRate: { name: '% Conversion (transactions)', fill: '#f97316' }
    };
    const config = configs[selectedBarMetric] || configs.conversions;
    return { dataKey: 'barMetricValue', ...config };
  }, [selectedBarMetric]);

  // Si en chargement ou pas de données, retourner null
  if (loading || !summary) {
    return null;
  }

  // Style du conteneur crème pour chaque section
  const sectionStyle = {
    background: '#FAF9F5',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px'
  };

  return (
    <>
      {/* Section Résultats */}
      <div style={sectionStyle}>
        <div className="section-header">
          <h4 style={{ fontSize: '18px', fontWeight: '600' }}>
            <TrendingUp size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Résultats
          </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Boutons de granularité */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {[
              { value: 'day', label: 'Jour' },
              { value: 'week', label: 'Semaine' },
              { value: 'month', label: 'Mois' },
              { value: 'quarter', label: 'Trimestre' },
              { value: 'year', label: 'Année' }
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => onGranularityChange?.(value)}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: granularity === value ? '#1a1a1a' : 'white',
                  color: granularity === value ? 'white' : '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                title={`Afficher par ${label.toLowerCase()}`}
              >
                {label}
              </button>
            ))}
          </div>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Résultats Piano"
            />
          ) : (
            <button
              onClick={() => setShowPerformanceChart(!showPerformanceChart)}
              className="section-toggle-btn"
              title={showPerformanceChart ? "Masquer la section" : "Afficher la section"}
            >
              {showPerformanceChart ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {showPerformanceChart && (
        <>
          {/* Message si pas de données */}
          {(!data || data.length === 0) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px',
              backgroundColor: '#f3f4f6',
              borderRadius: '8px',
              marginBottom: '12px',
              color: '#6b7280',
              fontSize: '14px',
              border: '1px solid #e5e7eb'
            }}>
              <AlertCircle size={16} style={{ marginRight: '8px', opacity: 0.6 }} />
              Aucune donnée disponible pour cette période
            </div>
          )}

          {/* Graphique */}
          <div className="full-width-chart" style={{
            width: '100%',
            minWidth: '300px',
            height: '400px',
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}>
            <ResponsiveContainer width="100%" height="100%" debounce={50}>
              <ComposedChart
                data={chartData}
                margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  type="category"
                  allowDuplicatedCategory={false}
                  tickFormatter={(value) => formatDateLabel(value, granularity)}
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  stroke={currentLineConfig.color}
                  tick={{ fontSize: 12 }}
                  label={{ value: currentLineConfig.label, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#8884d8"
                  tick={{ fontSize: 12 }}
                  label={{ value: barMetricConfig.name, angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value, name) => {
                    if (name === currentLineConfig.label) {
                      return [currentLineConfig.format(value) + currentLineConfig.unit, name];
                    }
                    /*
                      La série en barres n'avait aucun formatage propre : elle retombait sur
                      `formatNumber(value)`, c'est-à-dire un entier. Toute métrique de ratio
                      choisie pour l'histogramme s'affichait donc « 0 » dans l'infobulle — un
                      CPC de 0,0164 € ou un coût par interaction de 0,016 €. La carte, elle,
                      montrait 0,02 €, et les deux se contredisaient à l'écran.

                      Le formatage est repris de `lineMetricConfig`, la table qui porte déjà
                      l'unité et le nombre de décimales de chaque métrique. Une seconde table
                      propre aux barres aurait fini par diverger de la première.
                    */
                    const configBarre = lineMetricConfig[selectedBarMetric];
                    if (configBarre && name === barMetricConfig.name) {
                      return [configBarre.format(value) + configBarre.unit, name];
                    }
                    return [formatNumber(value), name];
                  }}
                />
                <Legend />

                {/* Repères des notes. Placés après la légende pour passer au-dessus des
                    séries, comme sur les dashboards de régies. */}
                {marqueursDeNotes({
                  notes,
                  donneesDuGraphique: chartData,
                  granularite: granularity,
                  onSurvol,
                  onSortie
                })}

                {/* Barres */}
                <Bar
                  yAxisId="right"
                  dataKey={barMetricConfig.dataKey}
                  fill={barMetricConfig.fill}
                  fillOpacity={0.8}
                  name={barMetricConfig.name}
                  radius={[4, 4, 0, 0]}
                />

                {/* Courbe */}
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="lineMetricValue"
                  stroke={currentLineConfig.color}
                  strokeWidth={3}
                  name={currentLineConfig.label}
                  dot={{ fill: currentLineConfig.color, strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7, fill: currentLineConfig.color }}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>

          {/* L'infobulle est en position fixe : hors du graphique, elle n'est pas rognée
              par ses marges. */}
          <InfobulleDeNotes survolee={survolee} position={position} />
          </div>

          {/* KPI Cards - Grille 4 colonnes comme GA4 */}
          <div className="chart-insights" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '12px',
            marginTop: '16px'
          }}>
            {/* Sessions */}
            {/*
              Les cartes **dans l'ordre du modèle**.

              Elles étaient écrites à la suite, chacune sous son propre `isResultsMetricActive` : le
              modèle réglait donc lesquelles afficher, jamais dans quel ordre. C'est la forme que
              `GA4PerformanceChart` emploie déjà.
            */}
            {(resultsMetrics.order?.length ? resultsMetrics.order : resultsMetrics.metrics || []).map((metricKey) => {
              if (!isResultsMetricActive(metricKey)) return null;

              switch (metricKey) {
                case 'visites':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'visits') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'visits', 'visits');
                          return;
                        }
                        choisirMetriqueGraphe('visits', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'visits'
                    ? '3px solid #82ca9d'
                    : selectedLineMetric === 'visits'
                      ? '3px solid #82ca9d'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'visits' ? 'Affiché en barres' : selectedLineMetric === 'visits' ? 'Affiché en courbe' : 'Cliquez pour afficher'}
              >
                <KPICard
                  title="Sessions"
                  value={kpiData.totalVisits}
                  color="#82ca9d"
                  currentPeriodData={data?.map(d => d.visits) || []}
                  previousValue={prevSummary?.visits || prevSummary?.totalVisits}
                  comparisonLabel={comparisonLabel}
                />
              </div>
                  );
                case 'taux_conv':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'conversionRate') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'conversionRate', 'conversionRate');
                          return;
                        }
                        choisirMetriqueGraphe('conversionRate', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'conversionRate'
                    ? '3px solid #f59e0b'
                    : selectedLineMetric === 'conversionRate'
                      ? '3px solid #f59e0b'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'conversionRate' ? 'Affiché en barres' : selectedLineMetric === 'conversionRate' ? 'Affiché en courbe' : 'Cliquez pour afficher'}
              >
                <KPICard
                  title="Taux de Conversion"
                  value={kpiData.conversionRate}
                  unit="%"
                  color="#f59e0b"
                  currentPeriodData={data?.map(d => d.visits > 0 ? (d.conversions || 0) / d.visits * 100 : 0) || []}
                  previousValue={prevSummary?.conversionRate}
                  comparisonLabel={comparisonLabel}
                  formatter={(val) => formatNumber(val, 2)}
                  className={getMetricObjectiveClassName('taux_conv', kpiData.conversionRate, 'piano_results', accountId)}
                />
              </div>
                  );
                case 'conv':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'conversions') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'conversions', 'conversions');
                          return;
                        }
                        choisirMetriqueGraphe('conversions', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'conversions'
                    ? '3px solid #8884d8'
                    : selectedLineMetric === 'conversions'
                      ? '3px solid #8884d8'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'conversions' ? 'Affiché en barres' : selectedLineMetric === 'conversions' ? 'Affiché en courbe' : 'Cliquez pour afficher'}
              >
                <KPICard
                  title="Conversions"
                  value={kpiData.totalConversions}
                  color="#8884d8"
                  currentPeriodData={data?.map(d => d.conversions || 0) || []}
                  previousValue={prevSummary?.conversions}
                  comparisonLabel={comparisonLabel}
                />
              </div>
                  );
                case 'pages_vues':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'pageViews') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'pageViews', 'pageViews');
                          return;
                        }
                        choisirMetriqueGraphe('pageViews', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'pageViews'
                    ? '3px solid #10b981'
                    : selectedLineMetric === 'pageViews'
                      ? '3px solid #10b981'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'pageViews' ? 'Affiché en barres' : selectedLineMetric === 'pageViews' ? 'Affiché en courbe' : 'Cliquez pour afficher'}
              >
                <KPICard
                  title="Pages vues"
                  value={kpiData.totalPageviews}
                  color="#10b981"
                  currentPeriodData={data?.map(d => d.pageViews) || []}
                  previousValue={prevSummary?.pageViews || prevSummary?.totalPageviews}
                  comparisonLabel={comparisonLabel}
                />
              </div>
                  );
                case 'rebond':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'bounceRate') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'bounceRate', 'bounceRate');
                          return;
                        }
                        choisirMetriqueGraphe('bounceRate', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'bounceRate'
                    ? '3px solid #8b5cf6'
                    : selectedLineMetric === 'bounceRate'
                      ? '3px solid #8b5cf6'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'bounceRate' ? 'Affiché en barres' : selectedLineMetric === 'bounceRate' ? 'Affiché en courbe' : 'Cliquez pour afficher'}
              >
                <KPICard
                  title="Rebond"
                  value={kpiData.avgBounceRate}
                  unit="%"
                  color="#8b5cf6"
                  currentPeriodData={data?.map(d => d.bounceRate || 0) || []}
                  previousValue={prevSummary?.bounceRate}
                  comparisonLabel={comparisonLabel}
                  invertVariation={true}
                  formatter={(val) => formatNumber(val, 1)}
                  className={getMetricObjectiveClassName('rebond', kpiData.avgBounceRate, 'piano_results', accountId)}
                />
              </div>
                  );
                case 'duree_moy':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'avgTimeOnSite') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'avgTimeOnSite', 'avgTimeOnSite');
                          return;
                        }
                        choisirMetriqueGraphe('avgTimeOnSite', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'avgTimeOnSite'
                    ? '3px solid #f97316'
                    : selectedLineMetric === 'avgTimeOnSite'
                      ? '3px solid #f97316'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'avgTimeOnSite' ? 'Affiché en barres' : selectedLineMetric === 'avgTimeOnSite' ? 'Affiché en courbe' : 'Cliquez pour afficher'}
              >
                <KPICard
                  title="Durée moy."
                  value={kpiData.avgTimeOnSite}
                  color="#f97316"
                  currentPeriodData={data?.map(d => d.avgTimeOnSite || 0) || []}
                  previousValue={prevSummary?.avgTimeOnSite}
                  comparisonLabel={comparisonLabel}
                  formatter={(val) => formatTime(val)}
                  className={getMetricObjectiveClassName('duree_moy', kpiData.avgTimeOnSite, 'piano_results', accountId)}
                />
              </div>
                  );
                case 'valeur_conv':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'conversionsValue') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'conversionsValue', 'conversionsValue');
                          return;
                        }
                        choisirMetriqueGraphe('conversionsValue', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'conversionsValue'
                    ? '3px solid #06b6d4'
                    : selectedLineMetric === 'conversionsValue'
                      ? '3px solid #06b6d4'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'conversionsValue' ? 'Affiché en barres' : selectedLineMetric === 'conversionsValue' ? 'Affiché en courbe' : 'Cliquez pour afficher'}
              >
                <KPICard
                  title="Valeur Conv."
                  value={kpiData.totalConversionsValue}
                  unit="€"
                  color="#06b6d4"
                  currentPeriodData={data?.map(d => d.conversionsValue || d.revenue || 0) || []}
                  previousValue={prevSummary?.conversionsValue}
                  comparisonLabel={comparisonLabel}
                  formatter={(val) => formatNumber(val, 2)}
                />
              </div>
                  );
                case 'panier_moyen':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'avgOrderValue') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'avgOrderValue', 'avgOrderValue');
                          return;
                        }
                        choisirMetriqueGraphe('avgOrderValue', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'avgOrderValue'
                    ? '3px solid #ec4899'
                    : selectedLineMetric === 'avgOrderValue'
                      ? '3px solid #ec4899'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'avgOrderValue' ? 'Affiché en barres' : selectedLineMetric === 'avgOrderValue' ? 'Affiché en courbe' : 'Cliquez pour afficher'}
              >
                <KPICard
                  title="Panier moyen"
                  value={kpiData.avgOrderValue}
                  unit="€"
                  color="#ec4899"
                  currentPeriodData={data?.map(d => d.conversions > 0 ? (d.conversionsValue || 0) / d.conversions : 0) || []}
                  previousValue={prevSummary?.avgOrderValue}
                  comparisonLabel={comparisonLabel}
                  formatter={(val) => formatNumber(val, 2)}
                  className={getMetricObjectiveClassName('panier_moyen', kpiData.avgOrderValue, 'piano_results', accountId)}
                />
              </div>
                  );
                case 'visiteurs':
                  return (
                    <React.Fragment key={metricKey}>
              {carteMetrique({
                  cle: 'visiteurs',
                  champ: 'uniqueVisitors',
                  titre: 'Utilisateurs',
                  valeur: kpiData.uniqueVisitors ?? 0,
                  couleur: '#8b5cf6',
                  nonMesure: kpiData.uniqueVisitors === null,
                  /**
                   * La courbe trace les visiteurs **du jour**, le total est celui de la période.
                   *
                   * Les deux ne s'accordent pas, et c'est normal : la somme des quatorze journées
                   * dépasse le total, un visiteur revenu cinq jours comptant cinq fois. Le total
                   * affiché est celui que Piano dédoublonne sur la période entière ; la courbe, elle,
                   * dit la progression jour après jour, ce qu'aucun total ne montre.
                   */
                  serie: (d) => d.visitors || 0,
                  mention: kpiData.uniqueVisitors === null
                    ? 'Visiteurs uniques non mesurés sur cette période'
                    : 'Total dédoublonné sur la période ; la courbe montre les visiteurs de chaque jour'
                })}
                    </React.Fragment>
                  );
                case 'pages_visite':
                  return (
                    <React.Fragment key={metricKey}>
              {carteMetrique({
                  cle: 'pages_visite',
                  champ: 'actionsPerVisit',
                  titre: 'Pages/Session',
                  valeur: kpiData.avgActionsPerVisit,
                  couleur: '#14b8a6',
                  decimales: 1,
                  serie: (d) => ((d.visits || 0) > 0 ? (d.pageViews || 0) / d.visits : 0)
                })}
                    </React.Fragment>
                  );
                case 'transactions':
                  return (
                    <React.Fragment key={metricKey}>
              {carteMetrique({
                  cle: 'transactions',
                  champ: 'transactions',
                  titre: 'Transactions',
                  valeur: kpiData.totalTransactions,
                  couleur: '#10b981'
                })}
                    </React.Fragment>
                  );
                case 'chiffre_affaires':
                  return (
                    <React.Fragment key={metricKey}>
              {carteMetrique({
                  cle: 'chiffre_affaires',
                  champ: 'totalRevenue',
                  titre: 'Chiffre d\'affaires',
                  valeur: kpiData.totalRevenue,
                  couleur: '#06b6d4',
                  unite: '€',
                  decimales: 2
                })}
                    </React.Fragment>
                  );
                case 'taux_transaction':
                  return (
                    <React.Fragment key={metricKey}>
              {carteMetrique({
                  cle: 'taux_transaction',
                  champ: 'transactionRate',
                  titre: '% Conversion (transactions)',
                  valeur: kpiData.transactionRate,
                  couleur: '#f97316',
                  unite: '%',
                  decimales: 2,
                  serie: (d) => ((d.visits || 0) > 0 ? (d.transactions || 0) / d.visits * 100 : 0)
                })}
                    </React.Fragment>
                  );
                default:
                  return null;
              }
            })}

          </div>
        </>
      )}

      {/* Modal de configuration des métriques Résultats */}
      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId="piano_results"
          accountId={accountId}
          componentTitle="Résultats"
        />
      )}
      </div>

      {/* Composant Canaux */}
      <div style={sectionStyle}>
        <PianoChannelsChart
          channels={channels}
          previousChannels={previousPeriodData?.channels}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="piano_channels"
        />
      </div>

      {/* Composant Géographie */}
      <div style={sectionStyle}>
        <PianoGeographyChart
          geography={geography}
          previousGeography={previousPeriodData?.geography}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          siteId={siteId}
          token={token}
          period={period}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="piano_geography"
        />
      </div>

      {/* Composant Appareils */}
      <div style={sectionStyle}>
        <RepartitionChart
          lignes={devices}
          lignesPrecedentes={previousPeriodData?.devices}
          cleDimension="device"
          cleLibelle="deviceLabel"
          titre="Appareils"
          intituleColonne="Appareil"
          nomAuSingulier="appareil"
          icone={Monitor}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          componentId="piano_devices"
          moduleId="piano_devices"
        />
      </div>

      {/* Composant Navigateurs */}
      <div style={sectionStyle}>
        <RepartitionChart
          lignes={browsers}
          lignesPrecedentes={previousPeriodData?.browsers}
          cleDimension="browser"
          cleLibelle="browserLabel"
          titre="Navigateurs"
          intituleColonne="Navigateur"
          nomAuSingulier="navigateur"
          icone={Globe}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          componentId="piano_browsers"
          moduleId="piano_browsers"
        />
      </div>

      {/* Composant Langues */}
      <div style={sectionStyle}>
        <RepartitionChart
          lignes={languages}
          lignesPrecedentes={previousPeriodData?.languages}
          cleDimension="language"
          cleLibelle="languageLabel"
          titre="Langues"
          intituleColonne="Langue"
          nomAuSingulier="langue"
          icone={Languages}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          componentId="piano_languages"
          moduleId="piano_languages"
        />
      </div>

      {/* Composant Jours de la semaine */}
      <div style={sectionStyle}>
        <RepartitionChart
          lignes={weekdays}
          lignesPrecedentes={previousPeriodData?.weekdays}
          cleDimension="weekday"
          cleLibelle="weekdayLabel"
          titre="Jours de la semaine"
          intituleColonne="Jour"
          nomAuSingulier="jour"
          /* Lundi à dimanche : un classement par volume ne se lirait pas comme une semaine. */
          triInitial={{ key: null, direction: 'desc' }}
          /* Deux séries de barres sur deux axes, comme `WeekdaysTable` côté Google Analytics. */
          graphe="barres"
          icone={Calendar}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          componentId="piano_weekdays"
          moduleId="piano_weekdays"
        />
      </div>

      {/* Composant Pages */}
      <div style={sectionStyle}>
        <RepartitionChart
          lignes={pages}
          lignesPrecedentes={previousPeriodData?.pages}
          cleDimension="page"
          cleLibelle="pageLabel"
          titre="Pages"
          /*
           * Pas de graphique : cinquante pages ne se lisent pas en parts, et le chiffre
           * d'affaires d'une page est une contribution, pas une recette — un camembert en
           * ferait une répartition.
           */
          graphe="aucun"
          intituleColonne="Page"
          nomAuSingulier="page"
          icone={FileText}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          componentId="piano_pages"
          moduleId="piano_pages"
        />
      </div>
    </>
  );
};

export default PianoPerformanceChart;
